import {
  ConditionDictionary,
  ConfigCondition,
  OrderValues,
  RemoteConfig,
} from '@ca/common/types'
import { validateItem } from '@order/utils/validateItem'
import {
  ConfigConditionSchema,
  ConfigConditionsSchemas,
  SelectSchema,
} from './ConfigConditionSchemas'

/**
 * Checks if a condition is met.
 *
 * @returns if condition exists return boolean, if condition does not exist return undefined
 */
const checkConfigCondition = (
  condition: ConfigCondition | undefined,
  values: OrderValues,
  conditions: ConditionDictionary | undefined
) => {
  if (!condition || !conditions) {
    return undefined
  }
  return validateItem(
    condition,
    values.values,
    conditions,
    values.refraction || undefined
  ).result
}

const getConfigConditionsGroup = (
  group: string,
  config: RemoteConfig | undefined | null
) => {
  const arr = Object.values(config?.configConditions ?? {})
  return arr.filter((c) => c.name.startsWith(group))
}

export type ConfigConditionCheckFn = (name: string) => boolean | undefined

const checkConfigConditionsGroup = (
  check: ConfigConditionCheckFn,
  toCheck: ConfigCondition[]
) => {
  const results = toCheck.map((condition) => ({
    value: check(condition.name),
    name: condition.name,
  }))
  const valid = results.filter((r) => r.value)
  return {
    all: valid.length === toCheck.length,
    any: valid.length > 0,
    valid,
    results,
  }
}

export const getConfigConditionValue = <T extends ConfigConditionSchema>(
  name: string | undefined,
  schemaKey: T,
  config: RemoteConfig | undefined | null
) => {
  if (!config || !name) {
    return undefined
  }
  const condition = config?.configConditions?.[name]
  if (!condition) {
    return undefined
  }

  const schema = ConfigConditionsSchemas[schemaKey]
  const parsed = schema.safeParse(condition.value)

  if (!parsed.success) {
    return undefined
  }
  return parsed.data as SelectSchema<T>
}

const ConfigConditions = {
  check: checkConfigCondition,
  value: getConfigConditionValue,
  group: {
    check: checkConfigConditionsGroup,
    get: getConfigConditionsGroup,
  },
}

export default ConfigConditions
